import { ISectionState, ISectionTransition } from '../../types';
import Gizmo from '../gizmo';
import RootGizmo from '../root-gizmo';

interface IConstructor {
  rootGizmo: RootGizmo;
}

class SectionStateMachine {
  private _rootGizmo: RootGizmo;
  /**
   * List of states, we're going to create one state per section
   */
  private _states?: ISectionState[];
  /**
   * Current state of the state machine
   */
  private _currentState?: ISectionState;
  /**
   * History of states navigation
   */
  private _statesHistory: ISectionState[] = [];

  constructor({ rootGizmo }: IConstructor) {
    this._rootGizmo = rootGizmo;
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const states: ISectionState[] = [];
      const sectionGizmos = this._rootGizmo
        .getGizmos()
        .filter(g => g.getConfig().type === 'section');

      for (let i = 0; i < sectionGizmos.length; i++) {
        const sectionGizmo = sectionGizmos[i];
        const config = sectionGizmo.getConfig();
        const features = sectionGizmo.getFeatures();

        const state: ISectionState = {
          sectionFid: config.fid,
          gizmo: sectionGizmo,
          transitions: [],
          display: false,
        };

        if (
          config.ops?.section?.jumps &&
          Array.isArray(config.ops.section.jumps) &&
          config.ops.section.jumps.length > 0
        ) {
          // If the 'section' gizmo has jumps we're going to translate them to transitions
          for (const jump of config.ops.section.jumps) {
            if (jump.destinyFid) {
              const transition: ISectionTransition = {
                origin: config.fid,
                destiny: jump.destinyFid,
                flux: jump.flux,
              };

              state.transitions.push(transition);
            }
          }
        }

        if (!features.section?.last) {
          // If the section is no the last one we're going to add a transition without flux, we're
          // doing this so if no flux condition is fullfilled we're moving to the next section
          const nextGizmo = sectionGizmos[i + 1];

          if (nextGizmo) {
            const transition: ISectionTransition = {
              origin: state.gizmo.getConfig().fid,
              destiny: nextGizmo.getConfig().fid,
            };

            state.transitions.push(transition);
          }
        }

        if (i === 0) {
          state.display = true;
          // We set the first state as currenState
          this._currentState = state;
          this._statesHistory.push(state);
        }

        states.push(state);
      }

      this._states = states;

      resolve();
    });
  };

  public getStates = (): ISectionState[] | undefined => {
    return this._states;
  };

  public getCurrentState = (): ISectionState | undefined => {
    return this._currentState;
  };

  /**
   * Move to the next state
   * @returns
   */
  public next = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (this._currentState?.transitions && this._states) {
        const gizmo = this._currentState.gizmo as Gizmo;
        const features = gizmo.getFeatures();
        const flux = gizmo.getFlux();

        for (let i = 0; i < this._currentState.transitions.length; i++) {
          const transition = this._currentState.transitions[i];

          if (transition.flux && flux) {
            const result = await flux.evaluate({
              script: transition.flux,
            });

            if (result === true) {
              // If the flux condition evaluates to true we're going to 'jump' to the specified
              // state
              const nextState = this._states.find(
                s => s.sectionFid === transition.destiny
              );

              if (nextState) {
                // And we're going to set the display flag to true so we know we've "been" on
                // this state and the child gizmos should be displayed on the UI layer
                nextState.display = true;

                this._currentState = nextState;
                this._statesHistory.push(nextState);
                break;
              }
            }
          } else if (
            i === this._currentState.transitions.length - 1 &&
            features.section?.last !== true
          ) {
            // If we're on the last transition and we're not on the last section we're going to
            // move to the next state
            const nextState = this._states.find(
              s => s.sectionFid === transition.destiny
            );

            if (nextState) {
              nextState.display = true;
              this._currentState = nextState;
              this._statesHistory.push(nextState);
              break;
            }
          }
        }
      }

      resolve();
    });
  };

  /**
   * Move through all the states until arriving to the last one
   * @returns
   */
  public last = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      let lastSectionFid = this._currentState?.sectionFid;

      do {
        await this.next();
        const currentSectionFid = this._currentState?.sectionFid;

        if (currentSectionFid === lastSectionFid) {
          break;
        } else {
          lastSectionFid = currentSectionFid;
        }
      } while (true);

      resolve();
    });
  };

  // next
  // back
  // goTo

  public back = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (this._statesHistory.length > 0) {
        this._statesHistory.pop();
        this._currentState = this._statesHistory[
          this._statesHistory.length - 1
        ];
      }

      resolve();
    });
  };
}

export default SectionStateMachine;
